import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useAnimations, useGLTF } from '@react-three/drei';
import { GroupProps, useThree } from '@react-three/fiber';
import gsap from 'gsap';
import caseGltf from '../../../../../../static/gltf/Case/Case-with-sticker.glb';

const MODEL_URL = caseGltf;

function Case(props: GroupProps) {
    const group = useRef<THREE.Group>(null);
    const { nodes, materials, animations } = useGLTF(MODEL_URL, true) as any;
    const { actions } = useAnimations(animations, group);
    const renderer = useThree((state) => state.gl);
    const [opened, setOpened] = useState(false);
    const scaleVec = Array.isArray(props.scale) ? new THREE.Vector3().fromArray(props.scale) : new THREE.Vector3(1);
    scaleVec.multiplyScalar(0.12);
    const animationToggleBtn = document.querySelector<HTMLElement>('.js-product-canvas-toggle-open-btn');

    if (nodes.Cube012_3) {
        nodes.Cube012_3.material.map.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
    }

    if (nodes.Cube011_4) {
        nodes.Cube011_4.material.map.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
    }

    const animationProgress = useRef(0);

    useEffect(() => {
        let tween: gsap.core.Tween | null;

        const onAnimationToggleBtnClick = () => {
            setOpened((prevOpened) => {
                const newOpened = !prevOpened;

                if (actions.input) {
                    actions.input.clampWhenFinished = true;
                    actions.input.loop = THREE.LoopOnce;

                    tween = gsap.to(animationProgress, {
                        current: newOpened ? 1 : 0,
                        duration: 2,
                        ease: 'none',
                        onUpdate: () => {
                            if (actions.input) {
                                actions.input.time = actions.input.getClip().duration * animationProgress.current;
                                actions.input.play();
                            }
                        },
                    });
                }

                return newOpened;
            });
        };

        animationToggleBtn?.addEventListener('click', onAnimationToggleBtnClick);

        return () => {
            animationToggleBtn?.removeEventListener('click', onAnimationToggleBtnClick);

            if (tween) {
                tween.kill();
            }
        };
    }, []);

    useEffect(() => {
        if (animationToggleBtn) {
            const text = animationToggleBtn.dataset[opened ? 'closeText' : 'openText'];
            animationToggleBtn.textContent = text || '';
        }
    }, [opened]);

    return (
        <group ref={group} {...props} dispose={null} scale={scaleVec} position={[0, -0.2, 0]}>
            <group name="Scene" rotation={[0, -Math.PI / 2, 0]}>
                <group name="Daigo_web_chemodan_nonlinglb">
                    <group name="Main" position={[-0.003, 0.299, 0.051]} rotation={[0, 0, -Math.PI / 2]}>
                        <mesh name="Cube012" geometry={nodes.Cube012.geometry} material={nodes.Cube012.material} />
                        <mesh
                            name="Cube012_1"
                            geometry={nodes.Cube012_1.geometry}
                            material={nodes.Cube012_1.material}
                        />
                        <mesh
                            name="Cube012_2"
                            geometry={nodes.Cube012_2.geometry}
                            material={nodes.Cube012_2.material}
                        />
                        <mesh
                            name="Cube012_3"
                            geometry={nodes.Cube012_3.geometry}
                            material={nodes.Cube012_3.material}
                        />
                    </group>
                    <group name="Bottom_DRAWER" position={[0, 0.299, 0.051]} rotation={[0, 0, -Math.PI / 2]}>
                        <mesh name="Cube011" geometry={nodes.Cube011.geometry} material={nodes.Cube011.material} />
                        <mesh
                            name="Cube011_1"
                            geometry={nodes.Cube011_1.geometry}
                            material={nodes.Cube011_1.material}
                        />
                        <mesh
                            name="Cube011_2"
                            geometry={nodes.Cube011_2.geometry}
                            material={nodes.Cube011_2.material}
                        />
                        <mesh
                            name="Cube011_3"
                            geometry={nodes.Cube011_3.geometry}
                            material={nodes.Cube011_3.material}
                        />
                        <mesh
                            name="Cube011_4"
                            geometry={nodes.Cube011_4.geometry}
                            material={nodes.Cube011_4.material}
                        />
                    </group>
                    <group name="Top_lid" position={[-0.992, 1.02, 1.947]} rotation={[0, 0, -1.572]}>
                        <mesh name="Cube013" geometry={nodes.Cube013.geometry} material={nodes.Cube013.material} />
                        <mesh
                            name="Cube013_1"
                            geometry={nodes.Cube013_1.geometry}
                            material={nodes.Cube013_1.material}
                        />
                    </group>
                    <mesh
                        name="Join_loops"
                        castShadow
                        receiveShadow
                        geometry={nodes.Join_loops.geometry}
                        material={nodes.Join_loops.material}
                        position={[-0.991, 1.021, 1.947]}
                        rotation={[0, -0.001, 3.139]}
                    />
                    <mesh
                        name="Join_loops001"
                        castShadow
                        receiveShadow
                        geometry={nodes.Join_loops001.geometry}
                        material={nodes.Join_loops001.material}
                        position={[-0.989, 1.022, 1.943]}
                        rotation={[0.001, 0.001, -1.505]}
                    />
                    <mesh
                        name="Join_loops002"
                        castShadow
                        receiveShadow
                        geometry={nodes.Join_loops002.geometry}
                        material={nodes.Join_loops002.material}
                        position={[-0.991, 1.021, 1.947]}
                        rotation={[0, 0, -2.861]}
                    />
                    <mesh
                        name="Plane012"
                        castShadow
                        receiveShadow
                        geometry={nodes.Plane012.geometry}
                        material={nodes.Plane012.material}
                        position={[1.502, 1.061, 0.058]}
                        rotation={[0, 0, -1.177]}
                        scale={0.099}
                    />
                    <group name="Left_belt001" position={[0.992, 1.283, 0.776]} scale={-0.108}>
                        <mesh name="Plane037" geometry={nodes.Plane037.geometry} material={materials.belt} />
                        <mesh
                            name="Plane037_1"
                            geometry={nodes.Plane037_1.geometry}
                            material={materials['Gold_alpha.001']}
                        />
                        <mesh name="Plane037_2" geometry={nodes.Plane037_2.geometry} material={materials.Belt_mini} />
                    </group>
                    <group name="Right_belt001" position={[0.992, 1.283, -0.683]} scale={0.108}>
                        <mesh name="Plane041" geometry={nodes.Plane041.geometry} material={materials.belt} />
                        <mesh
                            name="Plane041_1"
                            geometry={nodes.Plane041_1.geometry}
                            material={materials['Gold_alpha.001']}
                        />
                        <mesh name="Plane041_2" geometry={nodes.Plane041_2.geometry} material={materials.Belt_mini} />
                    </group>
                    <mesh
                        name="Wooden_handle"
                        castShadow
                        receiveShadow
                        geometry={nodes.Wooden_handle.geometry}
                        material={materials.wood_plank}
                        position={[1.88, 0.451, 0.049]}
                        scale={0.088}
                    />
                </group>
            </group>
        </group>
    );
}

export default Case;
